import { ThemeKey } from "@smartsheet/design-tokens";
import { useForkRef } from "@smartsheet/lodestar-utils";
import React, { forwardRef, Ref, useEffect } from "react";
import { useTheme } from "../../../theme/utils/useTheme";
import { useOverflowTooltip } from "../../../util/useOverflowTooltip";
import { Tooltip, TooltipBody } from "../../tooltip";
import { ursaTypeRampDefinitions } from "../constants/ursaConstants";
import { TypeRampRoot } from "../styled/TypeRampV2Styles";
import { TypeRampV2Props } from "../types";

const elementIsScrollable = (elem: HTMLElement | null) => {
    return !!elem && (elem.offsetHeight < elem.scrollHeight || elem.offsetWidth < elem.scrollWidth);
};

export const TypeRampV2 = forwardRef((props: TypeRampV2Props, parentRef: Ref<HTMLElement>): JSX.Element => {
    const {
        renderAs = "span",
        children,
        clientId,
        omitTooltip,
        tooltipPlacement = "auto",
        system,
        variant,
        ...rest
    } = props;

    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    const RootElement: keyof JSX.IntrinsicElements = renderAs;

    const { showOverflowTooltip, tooltipProps, targetProps, elementToMeasureRef } = useOverflowTooltip({
        tooltipTargetRef: parentRef,
        elementIsOverflowing: elementIsScrollable,
        isDisabled: omitTooltip,
        placement: tooltipPlacement,
    });

    const combinedRef = useForkRef(elementToMeasureRef, targetProps.ref);

    useEffect(() => {
        if (process.env.NODE_ENV !== "production") {
            const isUrsaVariant = variant in ursaTypeRampDefinitions;
            if (system === "lodestar" && isUrsaVariant) {
                console.warn(`The variant prop ${variant} doesn't apply to TypeRamp components 
                when the system='lodestar'. Please try passing system='ursa', or using a lodestar 
                compatible variant.`);
            }
        }
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    return (
        <TypeRampRoot
            as={RootElement}
            data-client-id={clientId}
            className="sds-type-ramp-v2"
            {...rest}
            {...(showOverflowTooltip && !omitTooltip ? targetProps : {})}
            ref={combinedRef}
            themeMode={themeMode}
            variant={variant}
            system={system}
        >
            {children}
            {showOverflowTooltip && !omitTooltip && (
                <Tooltip {...tooltipProps}>
                    <TooltipBody>{children}</TooltipBody>
                </Tooltip>
            )}
        </TypeRampRoot>
    );
});

TypeRampV2.displayName = "TypeRampV2";
